/* eslint import/no-unresolved: 0 */

import { useEffect, useState } from "react";
import { saveAffFromURL } from "utils/aff";

import Header from "components/Header";
import RecentResultsSlider from "components/RecentResultsSlider";
import Footer from "components/Footer";
import TodayPick from "components/home/TodayPick";
import { API_BASE } from "lib/http"; // 共通のAPI_BASEを使用

// 公開API（/api/public が正）
const API_PUBLIC = `${API_BASE}/api/public`;
// ログイン中ユーザー情報（/api/auth/me が正）
const API_ME = `${API_BASE}/api/auth/me`;

type NoticeItem = {
  id: string;
  title: string;
  body?: string;
  startAt?: string;
  createdAt?: string;
  level?: "info" | "warn" | "alert";
  linkUrl?: string;
};

type Me = { id: string; name?: string; points: number };

/** ISO文字列 → 2025年12月6日(土) 形式 */
const formatJPDate = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const w = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
  return `${y}年${m}月${day}日(${w})`;
};

export default function LP() {
  // LP流入時に aff コードを保存 ＋ ログイン中ユーザー取得
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    saveAffFromURL();

    (async () => {
      try {
        const r = await fetch(API_ME, { credentials: "include" });
        if (!r.ok) throw new Error(`ME ${r.status}`);
        const j = await r.json().catch(() => ({} as any));

        // レスポンス形の揺れ対応（{user:{...}} or そのまま {...}）
        const u: any = j?.user ?? j ?? {};

        // ID整形: 数字抽出 → 下6桁 → 先頭に 51 を付与
        const toDisplayId = (src: any): string | undefined => {
          const raw =
            src?.id ??
            src?.userId ??
            src?.uid ??
            src?.lineUserId ??
            src?.lineId ??
            "";
          const digits = String(raw).replace(/\D/g, "");
          if (!digits) return undefined;
          const last6 = digits.slice(-6).padStart(6, "0");
          return `51${last6}`;
        };

        const displayId = toDisplayId(u) ?? "";
        const displayName = (u?.lineName || u?.name || "").trim();
        const displayPoints = Number(
          typeof u?.points === "number"
            ? u.points
            : typeof u?.purchasePoint === "number"
            ? u.purchasePoint
            : 0
        );

        setMe({
          id: displayId, // Header 側でそのまま表示可能（数字文字列）
          name: displayName,
          points: displayPoints,
        });
      } catch {
        setMe(null);
      }
    })();
  }, []);

  // おすすめプランが1件以上あるかどうか（null = まだ判定前）
  const [hasRecommended, setHasRecommended] = useState<boolean | null>(null);

  // お知らせ（最新1件を使ってタイトル＋本文表示）
  const [news, setNews] = useState<NoticeItem[]>([]);

  // おすすめ判定 + お知らせ取得
  useEffect(() => {
    // ▼ おすすめプラン判定
    (async () => {
      try {
        const r = await fetch(`${API_PUBLIC}/recommended?t=${Date.now()}`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error(String(r.status));
        const j = await r.json();
        const rows = Array.isArray(j) ? j : [];
        setHasRecommended(rows.length > 0);
      } catch {
        // エラー時は「おすすめ無し扱い」で固定バナーを出す
        setHasRecommended(false);
      }
    })();

    // ▼ お知らせ取得
    (async () => {
      try {
        const r = await fetch(`${API_PUBLIC}/notices?t=${Date.now()}`, {
          credentials: "include",
        });
        const j = await r.json();
        const rows: NoticeItem[] = (Array.isArray(j) ? j : [])
          .slice(0, 1) // ★ 最新1件だけ
          .map((n: any) => ({
            id: String(n.id),
            title: String(n.title || ""),
            body: typeof n.body === "string" ? n.body : "",
            startAt: n.startAt || "",
            createdAt: n.createdAt || n.date || "",
            level: n.level || "info",
            linkUrl: n.linkUrl || "",
          }));
        setNews(rows);
      } catch {
        setNews([]);
      }
    })();
  }, []);

  const latest = news[0];
  const latestDate = formatJPDate(latest?.startAt || latest?.createdAt);
  const latestBodyLines: string[] =
    latest?.body
      ?.split(/\r?\n/)
      .map((s: string) => s.trim())
      .filter(Boolean) || []; // ★ 行数制限を外して全文表示

  // public 配下の背景パネル & ボタン画像
  const topicBg = `${process.env.PUBLIC_URL}/assets/member/topic.webp`;
  const resultsMoreBtn = `${process.env.PUBLIC_URL}/assets/member/red_button_jisseki_small.webp`;
  const fallbackTodayPick = `${process.env.PUBLIC_URL}/assets/member/EntryLP01.webp`;

  return (
    <main className="lp" style={{ background: "#0e1b26" }}>
      {/* ユーザー名・ID・ポイントをヘッダーへ渡す（UI構成そのまま） */}
      <Header userId={me?.id} userName={me?.name} points={me?.points} />

      {/* ▼ TodayPickの画像を“黄枠いっぱい”にフィットさせるための補助CSS */}
      <style>{`
        .todayPick-fill img {
          width: 100% !important;
          height: auto !important;
          display: block !important;
        }
        .todayPick-fill picture {
          display: block !important;
          width: 100% !important;
        }
      `}</style>

      {/* 本日の一押し（管理画面の “イチオシ” をそのまま表示） */}
      <section
        aria-label="本日の一押し"
        style={{ maxWidth: 720, margin: "0 auto", padding: "10px" }}
      >
        <div className="todayPick-fill">
          {hasRecommended === false ? (
            // ★ おすすめ0件 or エラー時は固定バナーを表示
            <img
              src={fallbackTodayPick}
              alt="BOAT億MAN 本日の一押し"
              style={{
                width: "100%",
                height: "auto",
                borderRadius: 8,
                display: "block",
              }}
            />
          ) : (
            // ★ 判定前(null) or 1件以上(true)なら従来どおり TodayPick を表示
            <TodayPick variant="lp" />
          )}
        </div>
      </section>

      {/* お知らせ＋実績セクション */}
      <section style={{ position: "relative" }}>
        <div
          className="stack"
          style={{ maxWidth: 720, margin: "0 auto", padding: "12px 10px" }}
        >
          {/* お知らせパネル（全体クリックで一覧へ） */}
          <a
            href="/notices"
            aria-label="お知らせ一覧へ"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <div
              className="topicPanel"
              style={{
                position: "relative",
                width: "100%",
                backgroundImage: `url(${topicBg})`,
                backgroundSize: "100% 100%",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                minHeight: 170,
                borderRadius: 12,
              }}
            >
              <div
                style={{
                  padding: 22,
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* 見出し */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#ffd54d",
                    fontWeight: 900,
                    fontSize: 16,
                    marginBottom: 8,
                    textShadow: "0 1px 2px rgba(0,0,0,.6)",
                  }}
                >
                  <span role="img" aria-label="megaphone">
                    📣
                  </span>
                  おしらせ
                </div>

                {/* 日付 → タイトル → 本文（行数制限なし） */}
                <div
                  style={{
                    color: "#e3f2fd",
                    fontSize: 14,
                    lineHeight: 1.4,
                    marginTop: 4,
                    marginBottom: 8,
                    textShadow: "0 1px 2px rgba(0,0,0,.4)",
                  }}
                >
                  {!latest ? (
                    <div style={{ color: "#cfd8dc", opacity: 0.9 }}>
                      現在お知らせはありません。
                    </div>
                  ) : (
                    <>
                      {latestDate && (
                        <div style={{ marginBottom: 4 }}>{latestDate}</div>
                      )}
                      {latest.title && (
                        <div style={{ marginBottom: 4 }}>{latest.title}</div>
                      )}
                      {latestBodyLines.length > 0 ? (
                        latestBodyLines.map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ))
                      ) : (
                        <div>お知らせの本文が登録されていません。</div>
                      )}
                    </>
                  )}
                </div>

                {/* 右下リンク */}
                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#ffd54d",
                      fontWeight: 700,
                      textDecoration: "underline",
                      textUnderlineOffset: 2,
                    }}
                  >
                    一覧を見る →
                  </span>
                </div>
              </div>
            </div>
          </a>

          {/* 実績スライダー */}
          <div style={{ marginTop: 16 }}>
            <RecentResultsSlider />
          </div>

          {/* 実績をもっと見るボタン（バッジの“下”に配置） */}
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <a
              href="/results"
              aria-label="実績をもっと見る"
              style={{ display: "inline-block" }}
            >
              <img
                src={resultsMoreBtn}
                alt="実績をもっと見る"
                style={{
                  width: "70%",
                  maxWidth: 320,
                  height: "auto",
                  display: "inline-block",
                }}
              />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
